import React from 'react';
import { withModal } from 'hoc';
import Form from 'components/form';
import { useTranslation } from 'i18n';
import { useHistory } from 'react-router-dom';
import useMutation from 'hooks/use-mutation';
import useSaveNodeInCache from 'components/canvas/hooks/use-save-node-in-cache';
import { createSurfaceMutation } from 'graphql/surface/mutations/create-surface';
import { updateSurfaceMutation } from 'graphql/surface-node/mutations/update-surface';
import { toast } from 'services/toast';
import { merge, pick } from 'lodash';
import { ModalContext, WithModalProps } from 'hoc/with-modal';
import { Header } from 'routes/surface/components/node-modal-form/style';
import { object, string } from 'yup';
import {
  SurfaceWrapper,
  NameInput,
} from 'routes/surface/components/surface-node-modal/style';
import { useSetHighestZIndexToNode } from 'components/canvas/hooks/use-set-heighest-z-index-to-node';

interface SurfaceFormProps extends WithModalProps {
  data: FormData;
  modal: ModalContext;
  disableClose?: boolean;
}

export interface FormData {
  id: string;
  name: string;
  surface: string;
}

function createValidationSchema(t) {
  return function () {
    return object().shape({
      name: string().required(t('name is required')),
    });
  };
}

function SurfaceFormContent({ data, modal, disableClose }: SurfaceFormProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const validationSchema = React.useMemo(createValidationSchema(t), []);

  const saveNodeInCache = useSaveNodeInCache();
  const setHighestZIndexToNode = useSetHighestZIndexToNode();

  const [createSurface] = useMutation(createSurfaceMutation);

  const [updateSurface] = useMutation(updateSurfaceMutation);

  const initialValues = merge(data ?? { name: '' });

  const submitLabel = data?.id ? t('Update cell') : t('Insert cell');

  function surfaceOperation(data) {
    const keys = data.id
      ? ['id', 'name']
      : ['id', 'name', 'surface', 'position'];

    const input = pick(data, keys);

    if (input.id) {
      return updateSurface({ variables: { input } });
    }

    return createSurface({ variables: { input } });
  }

  async function onSubmit(input) {
    const response = await surfaceOperation(input);

    if (response.isFailure) {
      return toast.error(response.getErrorMessage());
    }

    modal.hide();

    if (!input.surface) {
      // Close modal and navigate to newly created surface
      history.push('/surface/' + response.getField('data.id'));
    } else {
      saveNodeInCache(response.getField('data'));
      setHighestZIndexToNode(response.getField('data'));
    }

    if (input.id) {
      return toast.success(t('Surface successfully updated'));
    }

    return toast.success(t('Surface successfully created'));
  }

  return (
    <>
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}>
        {({ handleSubmit }) => (
          <>
            <Header>
              <Form.Button
                label={t('Close')}
                onClick={modal.hide}
                disabled={disableClose}
                secondary
              />
              <Form.SubmitButton label={submitLabel} onClick={handleSubmit} />
            </Header>
            <SurfaceWrapper>
              <NameInput
                name="name"
                autoFocus
                placeholder={t('Enter surface name')}
              />
            </SurfaceWrapper>
          </>
        )}
      </Form>
    </>
  );
}

export default withModal<SurfaceFormProps>(SurfaceFormContent);
